import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { CalendarEventModel, CreateCalendarEventDto } from '@/types/calendar';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

const METHOD = 'POST';
const ENDPOINT = 'calendar-events';

interface UseCreateCalendarEventOptions {
  onSuccess?: (data: CalendarEventModel) => void;
  onError?: (error: unknown) => void;
}

export const useCreateCalendarEvent = (options?: UseCreateCalendarEventOptions) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<CalendarEventModel, unknown, CreateCalendarEventDto>({
    mutationFn: async (data: CreateCalendarEventDto) => {
      try {
        return await handleApiCall<CalendarEventModel, CreateCalendarEventDto>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {}),
          data
        );
      } catch (err) {
        enqueueSnackbar('Failed to create calendar event.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: data => {
      // Invalidate queries related to calendar events
      queryClient.invalidateQueries({
        queryKey: [['GET', 'calendar-events']],
        exact: false,
      });

      if (options?.onSuccess) {
        options.onSuccess(data);
      }

      enqueueSnackbar('Calendar event created successfully.', { variant: 'success' });
    },
    onError: error => {
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};
